import React, { useCallback, useEffect, useMemo } from "react";
import {
  handleDeleteResponse,
  handleUpdateResponse,
  useDeleteLicensesMutation,
  useLazyRetrieveLicensesQuery,
  useRetrieveLicensePoolsQuery,
  useUpdateLicenseAddressesMutation,
} from "@/react/features/smp/licenses/licensesApi";
import { licenseColumnDefinition } from "@/react/features/smp/licenses/columnDefinition";
import TableComponent from "@/react/components/table/TableComponent";
import { licenseSchema } from "@/react/features/smp/licenses/schema";
import { errorNotification, useErrorNotification } from "@/react/notifications";
import { RowCountSelector } from "@/react/features/smp/licenses/components/RowCountSelector";
import { usePermissions } from "@/react/services/rbac/usePermissions";
import {
  LICENSES_DELETE,
  LICENSES_UPDATE,
} from "@/react/services/rbac/permissions";
import { useTable } from "@/react/components/table/useTable";
import { LICENSES_STATUS } from "@/react/features/smp/licenses/constants";
import TableWrapper from "@/react/features/styled/TableWrapper";

const columns = licenseColumnDefinition();

export function Licenses() {
  const { data: licensePools, error: licensePoolsError } =
    useRetrieveLicensePoolsQuery({
      opts: {
        unlimited: ["false"],
      },
    });

  useErrorNotification(licensePoolsError);

  const { hasPermission } = usePermissions();

  const [
    retrieveLicenses,
    { data: licenses, isFetching: isLoading, error: licensesError },
  ] = useLazyRetrieveLicensesQuery();

  useErrorNotification(licensesError);

  const [deleteLicenses, { isLoading: isDeleting }] =
    useDeleteLicensesMutation();

  const [updateLicenseAddress, { isLoading: isUpdatingAddress }] =
    useUpdateLicenseAddressesMutation();

  const licensesWithPool = useMemo(() => {
    if (!licenses || !licensePools) return [];
    return licenses
      .map((license) => {
        return {
          ...license,
          licensePool: licensePools[license.licensePoolId],
        };
      })
      .filter((license) => !!license.licensePool);
  }, [licenses, licensePools]);

  const idKey = licenseSchema.fields.licenseId.spec.key;
  const statusKey = licenseSchema.fields.status.spec.key;

  const { table, filters, state, mutations } = useTable(
    licensesWithPool,
    columns,
    licenseSchema,
    idKey,
    {
      enableRowSelection: (row) =>
        row.getValue(statusKey) === LICENSES_STATUS.PENDING_CONFIGURATION,
    }
  );

  useEffect(() => {
    fetch();
  }, [filters.displayFilters]);

  const fetch = (useCache = true) => {
    const statusFilterValue = filters.displayFilters.get(statusKey)?.value;
    const status = statusFilterValue ? [statusFilterValue] : [];

    retrieveLicenses(
      {
        opts: {
          status,
        },
      },
      useCache
    );
  };

  const {
    rowsToDelete,
    rowsToUpdate,
    setData,
    setRowsToDelete,
    setRowsToUpdate,
  } = mutations;

  const onSave = useCallback(() => {
    if (rowsToDelete.size > 0) {
      deleteLicenses(Array.from(rowsToDelete.keys()))
        .unwrap()
        .then(
          handleDeleteResponse({
            rowsToDelete,
            setRowsToDelete,
            setData,
          })
        )
        .catch(errorNotification);
    }

    if (rowsToUpdate.size > 0) {
      const licenseAddressIdPairs = Array.from(rowsToUpdate.entries()).map(
        ([licenseId, license]) => {
          return {
            licenseId,
            addressId: license.serviceAddressId,
          };
        }
      );
      updateLicenseAddress(licenseAddressIdPairs)
        .unwrap()
        .then(
          handleUpdateResponse({
            rowsToUpdate,
            setRowsToUpdate,
            setData,
          })
        )
        .catch(errorNotification);
    }
  }, [rowsToDelete, rowsToUpdate]);

  const actionsSlot = useCallback(
    (table) => (
      <RowCountSelector
        table={table}
        idKey={idKey}
        rowSelection={state.rowSelection}
        setRowSelection={state.setRowSelection}
      />
    ),
    [idKey, state.rowSelection, state.setRowSelection]
  );

  const isDirty = rowsToDelete.size > 0 || rowsToUpdate.size > 0;

  const isTableLoading = isLoading || isDeleting || isUpdatingAddress;

  if (!hasPermission(LICENSES_DELETE)) {
    mutations.deleteRows = false;
  }

  const canUpdateAddress = hasPermission(LICENSES_UPDATE);

  const addressColumn = columns.find(
    (column) => column.id === licenseSchema.fields.serviceAddress.spec.key
  );
  addressColumn.editable = canUpdateAddress;

  return (
    <TableWrapper>
      <TableComponent
        title="Licenses"
        schema={licenseSchema}
        isDirty={isDirty}
        isLoading={isTableLoading}
        onSave={onSave}
        onCancel={mutations.resetData}
        onRefresh={() => fetch(false)}
        actionsSlot={actionsSlot}
        table={table}
        filters={filters}
        mutations={mutations}
        state={state}
      />
    </TableWrapper>
  );
}
